import { weekdays } from '@/constants'
import { SharedProps, Zone } from '@/types'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { ActionIcon, Badge, Button, Group, Menu, Paper, Stack, Table, Text, ThemeIcon, Title } from '@mantine/core'
import { IconDotsVertical, IconPencil, IconPlus, IconRoute, IconTrash } from '@tabler/icons-react'

function ServiceDaysStrip({ serviceDays }: { serviceDays: number[] }) {
  return (
    <Group gap={4}>
      {weekdays.map((day) => (
        <Badge key={day.value} size="sm" radius="sm" variant={serviceDays.includes(day.value) ? 'filled' : 'default'}>
          {day.label}
        </Badge>
      ))}
    </Group>
  )
}

export default function ZonesIndex({ zones }: SharedProps & { zones: Zone[] }) {
  const { current_organization } = usePage<SharedProps>().props
  const base = `/organizations/${current_organization!.slug}`

  const handleEdit = (zone: Zone) => router.get(`${base}/zones/${zone.id}/edit`)
  const handleDelete = (zone: Zone) => {
    if (confirm(`Supprimer la zone « ${zone.name} » ? Ses boutiques ne seront plus rattachées à une zone.`))
      router.delete(`${base}/zones/${zone.id}`)
  }

  return (
    <>
      <Head title={`Zones - ${current_organization!.name}`} />

      <Stack>
        <Group justify="space-between" align="flex-start">
          <Stack gap={4}>
            <Title>Zones</Title>
            <Text c="dimmed" size="sm">
              Regroupez vos boutiques par zone et choisissez les jours où le coursier les dessert.
            </Text>
          </Stack>
          <Button component={Link} href={`${base}/zones/new`} leftSection={<IconPlus size={18} />}>
            Nouvelle zone
          </Button>
        </Group>

        {zones.length === 0 ? (
          <Paper withBorder radius="md" p="xl">
            <Stack align="center" gap="sm">
              <ThemeIcon variant="light" color="gray" size="xl" radius="xl">
                <IconRoute size={24} />
              </ThemeIcon>
              <Text c="dimmed" ta="center">
                Aucune zone pour le moment.
              </Text>
              <Button component={Link} href={`${base}/zones/new`} leftSection={<IconPlus size={16} />} variant="light">
                Créer une zone
              </Button>
            </Stack>
          </Paper>
        ) : (
          <Paper withBorder radius="md" p="md">
            <Table highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Zone</Table.Th>
                  <Table.Th>Jours de tournée</Table.Th>
                  <Table.Th style={{ width: 40 }} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {zones.map((zone) => (
                  <Table.Tr key={zone.id} onClick={() => handleEdit(zone)} style={{ cursor: 'pointer' }}>
                    <Table.Td fw={500}>{zone.name}</Table.Td>
                    <Table.Td>
                      <ServiceDaysStrip serviceDays={zone.service_days} />
                    </Table.Td>
                    <Table.Td onClick={(event) => event.stopPropagation()}>
                      <Menu shadow="md" width={200} position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="subtle" color="gray">
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>

                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => handleEdit(zone)}>
                            Modifier
                          </Menu.Item>
                          <Menu.Item
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            onClick={() => handleDelete(zone)}
                          >
                            Supprimer
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </Stack>
    </>
  )
}
